import { Button, Chip, Input, Select } from '@emailforge/ui';
import { useState } from 'react';

import { TopBar } from '@/components/layout/TopBar';
import { Icon } from '@/components/ui/icons';
import {
  type CredibleInterval,
  type ExperimentDetail,
  useCancelExperimentMutation,
  useConversionTimeSeriesQuery,
  useCreateExperimentMutation,
  useDeclareWinnerMutation,
  useGetExperimentQuery,
  useListExperimentsQuery,
  useStartExperimentMutation,
} from '@/features/experiments/experimentsApi';
import { useListTemplatesQuery } from '@/features/templates/templatesApi';
import { t } from '@/lib/i18n';

export function ABScreen() {
  const { data: experiments, isLoading } = useListExperimentsQuery();
  const [showForm, setShowForm] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: detail } = useGetExperimentQuery(selectedId!, {
    skip: !selectedId,
    pollingInterval: selectedId ? 5000 : 0,
  });

  return (
    <>
      <TopBar
        crumbs={[t('crumbs.experiments')]}
        right={
          !showForm ? (
            <Button variant="flame" size="sm" onClick={() => setShowForm(true)}>
              <Icon.plus size={13} /> New experiment
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
          )
        }
      />

      <div style={{ flex: 1, overflowY: 'auto', background: 'var(--paper-2)' }}>
        {showForm ? (
          <div style={{ padding: 24, maxWidth: 720, margin: '0 auto' }}>
            <NewExperimentForm onDone={() => setShowForm(false)} />
          </div>
        ) : (
          <div
            style={{
              padding: 24,
              maxWidth: 1100,
              margin: '0 auto',
              display: 'grid',
              gridTemplateColumns: detail ? '1fr 1.4fr' : '1fr',
              gap: 16,
            }}
          >
            <div>
              <h1 className="display" style={{ fontSize: 28, margin: '0 0 14px' }}>
                A/B experiments
              </h1>

              {isLoading && (
                <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                  Loading…
                </div>
              )}

              {!isLoading && (experiments ?? []).length === 0 && (
                <div className="mf-card" style={{ padding: 32, textAlign: 'center' }}>
                  <div
                    className="mono"
                    style={{
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: 0.08,
                      color: 'var(--ink-3)',
                    }}
                  >
                    No experiments yet
                  </div>
                  <h2 className="display" style={{ fontSize: 20, margin: '8px 0' }}>
                    Run your first A/B test
                  </h2>
                  <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: '0 auto 16px', maxWidth: 360 }}>
                    Split traffic across variants, track opens/clicks/conversions, let the Bayesian model pick a winner.
                  </p>
                  <Button variant="flame" size="sm" onClick={() => setShowForm(true)}>
                    <Icon.plus size={13} /> New experiment
                  </Button>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {experiments?.map((e) => (
                  <button
                    key={e.id}
                    className="mf-card"
                    onClick={() => setSelectedId(e.id)}
                    style={{
                      padding: 14,
                      textAlign: 'left',
                      cursor: 'pointer',
                      border: selectedId === e.id ? '1px solid var(--flame)' : '1px solid var(--line)',
                      background: selectedId === e.id ? 'var(--flame-soft)' : 'var(--paper)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 550, flex: 1 }}>{e.name}</div>
                      <Chip
                        variant={
                          e.status === 'running'
                            ? 'live'
                            : e.status === 'finished'
                              ? 'flame'
                              : e.status === 'cancelled'
                                ? 'err'
                                : 'draft'
                        }
                      >
                        {e.status}
                      </Chip>
                    </div>
                    <div
                      className="mono"
                      style={{
                        fontSize: 10.5,
                        color: 'var(--ink-3)',
                        textTransform: 'uppercase',
                        letterSpacing: 0.06,
                      }}
                    >
                      metric: {e.metric} · target: {e.sampleSizeTarget}
                      {e.winnerVariantId && ` · winner: ${e.winnerVariantId.toUpperCase()}`}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {detail && <ExperimentPanel detail={detail} />}
          </div>
        )}
      </div>
    </>
  );
}

function ExperimentPanel({ detail }: { detail: ExperimentDetail }) {
  const [start, { isLoading: isStarting }] = useStartExperimentMutation();
  const [cancel] = useCancelExperimentMutation();
  const [declareWinner, { isLoading: isDeclaring }] = useDeclareWinnerMutation();

  return (
    <div className="mf-card" style={{ padding: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <div style={{ fontSize: 16, fontWeight: 600, flex: 1 }}>{detail.name}</div>
        {detail.status === 'draft' && (
          <Button variant="flame" size="sm" onClick={() => start(detail.id)} disabled={isStarting}>
            {isStarting ? 'Starting…' : 'Start experiment'}
          </Button>
        )}
        {detail.status === 'running' && (
          <>
            <Button size="sm" onClick={() => declareWinner({ id: detail.id })} disabled={isDeclaring}>
              Declare leader as winner
            </Button>
            <Button variant="ghost" size="sm" onClick={() => cancel(detail.id)}>
              Cancel
            </Button>
          </>
        )}
      </div>

      {detail.hypothesis && (
        <p style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 0 }}>
          <span
            className="mono"
            style={{ fontSize: 10.5, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: 0.06, marginRight: 6 }}
          >
            Hypothesis
          </span>
          {detail.hypothesis}
        </p>
      )}

      <div
        className="mono"
        style={{
          fontSize: 10.5,
          color: 'var(--ink-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          marginBottom: 8,
        }}
      >
        Progress · {detail.totalAssignments} / {detail.sampleSizeTarget}
      </div>
      <div
        style={{
          height: 6,
          background: 'var(--paper-4)',
          borderRadius: 'var(--r-pill)',
          overflow: 'hidden',
          marginBottom: 20,
        }}
      >
        <div
          style={{
            width: `${Math.round(detail.progress * 100)}%`,
            height: '100%',
            background: 'var(--flame)',
            transition: 'width 500ms ease',
          }}
        />
      </div>

      <div
        className="mono"
        style={{
          fontSize: 10.5,
          color: 'var(--ink-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          marginBottom: 10,
        }}
      >
        Variants · metric: {detail.metric}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {detail.stats.map((s) => {
          const prob = detail.bayesianProb[s.id] ?? 0;
          const ci = detail.credibleIntervals?.[s.id];
          const isLeader = detail.leader?.id === s.id;
          const rateKey =
            detail.metric === 'open' ? 'openRate' : detail.metric === 'click' ? 'clickRate' : 'conversionRate';
          const rate = s[rateKey];
          return (
            <div
              key={s.id}
              className="mf-card"
              style={{
                padding: 14,
                borderColor: isLeader ? 'var(--flame)' : 'var(--line)',
                background: isLeader ? 'var(--flame-soft)' : 'var(--paper)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <div style={{ fontSize: 13.5, fontWeight: 550, flex: 1 }}>{s.label}</div>
                {detail.winnerVariantId === s.id && (
                  <Chip variant="flame" dot={false}>
                    <Icon.flame size={10} /> winner
                  </Chip>
                )}
                {!detail.winnerVariantId && isLeader && (
                  <Chip variant="flame">leading
                  </Chip>
                )}
                <span
                  className="mono"
                  style={{ fontSize: 10.5, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: 0.06 }}
                >
                  weight {s.weight}%
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
                <StatTile label="Assigned" value={s.assignments.toString()} />
                <StatTile label="Open rate" value={`${(s.openRate * 100).toFixed(1)}%`} />
                <StatTile label="Click rate" value={`${(s.clickRate * 100).toFixed(1)}%`} />
                <StatTile label="Conversions" value={`${(s.conversionRate * 100).toFixed(1)}%`} />
                <StatTile
                  label="Prob. best"
                  value={`${(prob * 100).toFixed(0)}%`}
                  accent={prob > 0.5 ? 'var(--flame)' : undefined}
                />
              </div>

              {detail.status === 'running' && (
                <div style={{ display: 'flex', gap: 6, marginTop: 10, justifyContent: 'flex-end' }}>
                  <Button variant="ghost" size="sm" onClick={() => declareWinner({ id: detail.id, variantId: s.id })}
                  >
                    Declare {s.label} as winner
                  </Button>
                </div>
              )}

              <div
                className="mono"
                style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 8, letterSpacing: 0.04 }}
              >
                {rateKey}: {(rate * 100).toFixed(2)}% · Bayesian P(best) = {(prob * 100).toFixed(1)}%
                {ci && (
                  <>
                    {' · '}95% CrI = [{(ci.lower * 100).toFixed(1)}%, {(ci.upper * 100).toFixed(1)}%]
                  </>
                )}
              </div>

              {ci && <CredibleIntervalBar ci={ci} />}
            </div>
          );
        })}
      </div>

      <ConversionTimeSeriesCard experimentId={detail.id} variants={detail.variants} />
    </div>
  );
}

function ConversionTimeSeriesCard({
  experimentId,
  variants,
}: {
  experimentId: string;
  variants: ExperimentDetail['variants'];
}) {
  const { data: rows = [], isLoading } = useConversionTimeSeriesQuery(experimentId);
  if (isLoading || rows.length === 0) {
    return (
      <div style={{ marginTop: 20 }}>
        <div
          className="mono"
          style={{
            fontSize: 10.5,
            color: 'var(--ink-3)',
            textTransform: 'uppercase',
            letterSpacing: 0.06,
            marginBottom: 8,
          }}
        >
          Conversions · per hour
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
          {isLoading ? 'Loading…' : 'No conversions recorded yet. Call POST /api/experiments/:id/conversions when a recipient converts.'}
        </div>
      </div>
    );
  }

  const palette = ['var(--flame)', 'var(--ink-2)', 'var(--spark, #2c8a55)', 'var(--warn, #b48323)'];
  const variantOrder = variants.map((v) => v.id);
  const hourSet = new Set<string>();
  rows.forEach((r) => hourSet.add(r.hour));
  const hours = [...hourSet].sort();
  const series = variantOrder.map((vid) =>
    hours.map((h) => rows.find((r) => r.hour === h && r.variantId === vid)?.conversions ?? 0),
  );
  const max = Math.max(...series.flat(), 1);

  const width = 480;
  const height = 140;
  const stepX = hours.length > 1 ? width / (hours.length - 1) : width;

  return (
    <div style={{ marginTop: 22 }}>
      <div
        className="mono"
        style={{
          fontSize: 10.5,
          color: 'var(--ink-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          marginBottom: 10,
        }}
      >
        Conversions · per hour
      </div>
      <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ height }}>
        {[0, 1, 2, 3].map((i) => (
          <line
            key={i}
            x1={0}
            x2={width}
            y1={(i * height) / 3}
            y2={(i * height) / 3}
            stroke="var(--line-soft)"
            strokeWidth={1}
          />
        ))}
        {series.map((points, vi) => (
          <polyline
            key={variantOrder[vi]}
            points={points
              .map((v, i) => `${i * stepX},${height - 4 - (v / max) * (height - 14)}`)
              .join(' ')}
            fill="none"
            stroke={palette[vi % palette.length]}
            strokeWidth={1.6}
            strokeLinejoin="round"
          />
        ))}
      </svg>
      <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 11, color: 'var(--ink-3)', flexWrap: 'wrap' }}>
        {variantOrder.map((vid, vi) => {
          const variant = variants.find((v) => v.id === vid);
          return (
            <div key={vid} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  display: 'inline-block',
                  width: 10,
                  height: 2,
                  background: palette[vi % palette.length],
                }}
              />
              {variant?.label ?? vid}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Mini-bar that visualizes the 95% credibility interval over [0, max].
 * The shaded segment is the interval, the dot is the median. Width-relative
 * within the variant card so eyeballing "do the intervals overlap?" is the
 * point — overlap means the variants haven't separated yet, regardless of
 * what the leader chip claims.
 */
function CredibleIntervalBar({ ci }: { ci: CredibleInterval }) {
  // Anchor the axis at twice the upper bound (rounded up to the next 5/10/25%)
  // so we don't spend pixels visualizing the empty 80%+ of the [0,1] range.
  const ceiling = Math.max(0.05, niceCeiling(ci.upper * 1.15));
  const pct = (n: number) => `${Math.min(100, (n / ceiling) * 100)}%`;
  return (
    <div style={{ position: 'relative', height: 14, marginTop: 8 }}>
      <div
        style={{
          position: 'absolute',
          top: 6,
          left: 0,
          right: 0,
          height: 2,
          background: 'var(--paper-2)',
          borderRadius: 1,
        }}
      />
      <div
        style={{
          position: 'absolute',
          top: 4,
          left: pct(ci.lower),
          width: `calc(${pct(ci.upper)} - ${pct(ci.lower)})`,
          height: 6,
          background: 'var(--flame-soft)',
          borderRadius: 3,
          border: '1px solid var(--flame)',
        }}
        title={`95% CrI: [${(ci.lower * 100).toFixed(2)}%, ${(ci.upper * 100).toFixed(2)}%]`}
      />
      <div
        style={{
          position: 'absolute',
          top: 3,
          left: `calc(${pct(ci.median)} - 4px)`,
          width: 8,
          height: 8,
          background: 'var(--flame)',
          borderRadius: '50%',
        }}
        title={`median: ${(ci.median * 100).toFixed(2)}%`}
      />
    </div>
  );
}

function niceCeiling(n: number): number {
  for (const step of [0.05, 0.1, 0.25, 0.5, 0.75, 1]) {
    if (n <= step) return step;
  }
  return 1;
}

function StatTile({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div className="mf-card" style={{ padding: 10 }}>
      <div
        className="mono"
        style={{ fontSize: 9.5, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: 0.06 }}
      >
        {label}
      </div>
      <div
        className="display"
        style={{ fontSize: 18, fontWeight: 500, marginTop: 4, color: accent ?? 'var(--ink)' }}
      >
        {value}
      </div>
    </div>
  );
}

function NewExperimentForm({ onDone }: { onDone: () => void }) {
  const { data: templates } = useListTemplatesQuery();
  const [create, { isLoading, error }] = useCreateExperimentMutation();

  const [name, setName] = useState('');
  const [hypothesis, setHypothesis] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [metric, setMetric] = useState<'open' | 'click' | 'conversion'>('open');
  const [sampleSize, setSampleSize] = useState(1000);
  const [variants, setVariants] = useState([
    { id: 'a', label: 'Variant A', weight: 50 },
    { id: 'b', label: 'Variant B', weight: 50 },
  ]);

  const submit = async () => {
    await create({
      name,
      hypothesis: hypothesis || undefined,
      templateId,
      metric,
      variants,
      sampleSizeTarget: sampleSize,
    }).unwrap();
    onDone();
  };

  return (
    <div className="mf-card" style={{ padding: 24 }}>
      <h2 className="display" style={{ fontSize: 22, margin: '0 0 18px' }}>
        New A/B experiment
      </h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Name">
          <Input  value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Hypothesis (optional)">
          <Input
            
            value={hypothesis}
            onChange={(e) => setHypothesis(e.target.value)}
            placeholder="Shorter subject lines will get more opens" />
        </Field>
        <Field label="Template">
          <Select  value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
            <option value="">Pick a template…</option>
            {(templates ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Metric">
          <div style={{ display: 'flex', gap: 8 }}>
            {(['open', 'click', 'conversion'] as const).map((m) => (
              <label
                key={m}
                style={{
                  flex: 1,
                  padding: 10,
                  border: `1px solid ${metric === m ? 'var(--flame)' : 'var(--line)'}`,
                  borderRadius: 'var(--r-2)',
                  cursor: 'pointer',
                  background: metric === m ? 'var(--flame-soft)' : 'var(--paper)',
                  textAlign: 'center',
                  fontSize: 13,
                  fontWeight: 550,
                  textTransform: 'capitalize',
                }}
              >
                <input
                  type="radio"
                  checked={metric === m}
                  onChange={() => setMetric(m)}
                  style={{ display: 'none' }}
                />
                {m}
              </label>
            ))}
          </div>
        </Field>
        <Field label="Sample size target">
          <Input
            
            type="number"
            min={50}
            value={sampleSize}
            onChange={(e) => setSampleSize(Number(e.target.value))} />
        </Field>

        <Field label="Variants">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {variants.map((v, i) => (
              <div
                key={i}
                className="mf-card"
                style={{
                  padding: 10,
                  display: 'grid',
                  gridTemplateColumns: '80px 1fr 120px auto',
                  gap: 8,
                  alignItems: 'center',
                }}
              >
                <Input mono
                  
                  value={v.id}
                  maxLength={4}
                  onChange={(e) => setVariants(variants.map((x, j) => (j === i ? { ...x, id: e.target.value } : x)))} />
                <Input
                  
                  value={v.label}
                  onChange={(e) =>
                    setVariants(variants.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))
                  } />
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Input
                    
                    type="number"
                    min={0}
                    max={100}
                    value={v.weight}
                    onChange={(e) =>
                      setVariants(variants.map((x, j) => (j === i ? { ...x, weight: Number(e.target.value) } : x)))
                    } />
                  <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    %
                  </span>
                </div>
                {variants.length > 2 && (
                  <Button variant="ghost" size="sm" icon onClick={() => setVariants(variants.filter((_, j) => j !== i))}
                  >
                    <Icon.trash size={12} />
                  </Button>
                )}
              </div>
            ))}
            {variants.length < 4 && (
              <Button size="sm" onClick={() => {
                  const letter = String.fromCharCode('a'.charCodeAt(0) + variants.length);
                  setVariants([
                    ...variants,
                    { id: letter, label: `Variant ${letter.toUpperCase()}`, weight: 0 },
                  ]);
                }}
              >
                <Icon.plus size={12} /> Add variant
              </Button>
            )}
          </div>
        </Field>

        {error && (
          <div
            style={{
              background: 'var(--err-soft)',
              color: 'var(--err)',
              padding: '10px 12px',
              borderRadius: 'var(--r-2)',
              fontSize: 12.5,
              border: '1px solid oklch(0.85 0.10 25)',
            }}
          >
            {(error as { data?: { message?: string } }).data?.message ?? 'Validation failed'}
          </div>
        )}

        <Button variant="flame" onClick={submit}
          disabled={!name || !templateId || isLoading}
          style={{ marginTop: 6 }}>
          {isLoading ? 'Creating…' : 'Create experiment (draft)'}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label
        className="mono"
        style={{
          fontSize: 10.5,
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          color: 'var(--ink-3)',
          display: 'block',
          marginBottom: 6,
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}
